import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import DateInputWithArrows from './DateInputWithArrows';
import UpdateFileDueDateDialog from './UpdateFileDueDateDialog';
import UpdateFileNotesDialog from './UpdateFileNotesDialog';
import useResponsive from './responsive/useResponsive';
import {
  createProjectOtherDocument,
  deleteProjectOtherDocument,
  getProjectOtherDocumentContent,
  getProjectOtherDocuments,
  getProjectOtherDocumentUploadUrl,
  updateProjectOtherDocument,
} from '../services/api';
import { getApiErrorMessage } from '../utils/apiErrors';
import { formatDateEuropean } from '../utils/dateUtils';

const emptyForm = {
  name: '',
  expirationDate: '',
  notes: '',
  file: null,
};

function ProjectOtherDocumentsTab({ project }) {
  const { t, i18n } = useTranslation();
  const { isMobile } = useResponsive();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [uploading, setUploading] = useState(false);
  const [dueDateDialog, setDueDateDialog] = useState({ open: false, document: null });
  const [savingDueDate, setSavingDueDate] = useState(false);
  const [notesDialog, setNotesDialog] = useState({ open: false, document: null });
  const [savingNotes, setSavingNotes] = useState(false);

  const fetchDocuments = useCallback(async () => {
    if (!project?.id) return;
    setLoading(true);
    try {
      const response = await getProjectOtherDocuments(project.id);
      setDocuments(response.data || []);
    } catch (error) {
      alert(await getApiErrorMessage(error, error.message));
    } finally {
      setLoading(false);
    }
  }, [project?.id]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const closeDialog = () => {
    if (uploading) return;
    setDialogOpen(false);
    setForm(emptyForm);
  };

  const handleUpload = async () => {
    const name = form.name.trim();
    if (!project?.id || !name || !form.file) return;

    setUploading(true);
    try {
      const presignResponse = await getProjectOtherDocumentUploadUrl(project.id, {
        originalName: form.file.name,
        contentType: form.file.type || 'application/octet-stream',
      });
      const { uploadUrl, objectKey } = presignResponse.data;
      await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': form.file.type || 'application/octet-stream' },
        body: form.file,
      });
      await createProjectOtherDocument(project.id, {
        name,
        notes: form.notes.trim() || undefined,
        expirationDate: form.expirationDate || undefined,
        objectKey,
        originalName: form.file.name,
        mimeType: form.file.type || 'application/octet-stream',
        size: form.file.size || 0,
      });
      setDialogOpen(false);
      setForm(emptyForm);
      await fetchDocuments();
    } catch (error) {
      alert(await getApiErrorMessage(error, error.message));
    } finally {
      setUploading(false);
    }
  };

  const handlePreview = async (document) => {
    if (!project?.id || !document?.id) return;
    try {
      const response = await getProjectOtherDocumentContent(project.id, document.id);
      const objectUrl = URL.createObjectURL(response.data);
      const previewWindow = window.open(objectUrl, '_blank', 'noopener,noreferrer');
      if (!previewWindow) {
        URL.revokeObjectURL(objectUrl);
        alert(t('projectDetails.otherDocuments.previewFailed'));
        return;
      }
      previewWindow.addEventListener('beforeunload', () => URL.revokeObjectURL(objectUrl));
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    } catch (error) {
      alert(await getApiErrorMessage(error, t('projectDetails.otherDocuments.previewFailed')));
    }
  };

  const handleDownload = async (document) => {
    if (!project?.id || !document?.id) return;
    try {
      const response = await getProjectOtherDocumentContent(project.id, document.id, { download: true });
      const blobUrl = URL.createObjectURL(response.data);
      const link = window.document.createElement('a');
      link.href = blobUrl;
      link.download = document.originalName || document.name || 'document';
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      alert(await getApiErrorMessage(error, t('projectDetails.otherDocuments.downloadFailed')));
    }
  };

  const handleDelete = async (document) => {
    if (!project?.id || !document?.id) return;
    if (!window.confirm(t('projectDetails.otherDocuments.deleteConfirm'))) return;
    try {
      await deleteProjectOtherDocument(project.id, document.id);
      await fetchDocuments();
    } catch (error) {
      alert(await getApiErrorMessage(error, error.message));
    }
  };

  const closeDueDateDialog = () => {
    if (savingDueDate) return;
    setDueDateDialog({ open: false, document: null });
  };

  const handleSaveDueDate = async (expirationDate) => {
    if (!project?.id || !dueDateDialog.document?.id) return;
    setSavingDueDate(true);
    try {
      await updateProjectOtherDocument(project.id, dueDateDialog.document.id, { expirationDate });
      setDueDateDialog({ open: false, document: null });
      await fetchDocuments();
    } catch (error) {
      alert(await getApiErrorMessage(error, error.message));
    } finally {
      setSavingDueDate(false);
    }
  };

  const closeNotesDialog = () => {
    if (savingNotes) return;
    setNotesDialog({ open: false, document: null });
  };

  const handleSaveNotes = async (notes) => {
    if (!project?.id || !notesDialog.document?.id) return;
    setSavingNotes(true);
    try {
      await updateProjectOtherDocument(project.id, notesDialog.document.id, { notes });
      setNotesDialog({ open: false, document: null });
      await fetchDocuments();
    } catch (error) {
      alert(await getApiErrorMessage(error, error.message));
    } finally {
      setSavingNotes(false);
    }
  };

  const buildSecondaryText = (document) => {
    const parts = [
      document.originalName,
      `${t('projectDetails.uploadedAt')}: ${new Date(document.uploadedAt).toLocaleString(i18n.language)}`,
    ];
    if (document.expirationDate) {
      parts.push(`${t('personnel.files.expires')}: ${formatDateEuropean(document.expirationDate)}`);
    }
    if (document.notes) {
      parts.push(document.notes);
    }
    return parts.join(' | ');
  };

  const canSubmit = Boolean(form.name.trim() && form.file) && !uploading;

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setDialogOpen(true)}
        >
          {t('projectDetails.otherDocuments.addDocument')}
        </Button>
      </Box>

      {loading && documents.length === 0 ? (
        <Typography variant="body2" color="text.secondary">{t('common.loading')}</Typography>
      ) : documents.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('projectDetails.otherDocuments.noDocuments')}
        </Typography>
      ) : (
        <List dense>
          {documents.map((document) => (
            <ListItem
              key={document.id}
              alignItems="flex-start"
              sx={{ flexDirection: 'column', alignItems: 'stretch', px: 0 }}
            >
              <ListItemText
                primary={document.name}
                secondary={buildSecondaryText(document)}
                secondaryTypographyProps={{ component: 'span' }}
              />
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                <Button size="small" onClick={() => handlePreview(document)}>
                  {t('common.preview')}
                </Button>
                <Button size="small" variant="outlined" onClick={() => handleDownload(document)}>
                  {t('projectDetails.downloadDocument')}
                </Button>
                <Button size="small" onClick={() => setDueDateDialog({ open: true, document })}>
                  {t('common.updateDueDate')}
                </Button>
                <Button size="small" onClick={() => setNotesDialog({ open: true, document })}>
                  {t('common.updateNotes')}
                </Button>
                <Button size="small" color="error" onClick={() => handleDelete(document)}>
                  {t('common.delete')}
                </Button>
              </Stack>
            </ListItem>
          ))}
        </List>
      )}

      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        fullWidth
        maxWidth="sm"
        fullScreen={isMobile}
      >
        <DialogTitle>{t('projectDetails.otherDocuments.addDocument')}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            autoFocus
            required
            fullWidth
            margin="dense"
            label={t('projectDetails.otherDocuments.name')}
            value={form.name}
            onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
            disabled={uploading}
          />
          <DateInputWithArrows
            fullWidth
            label={t('personnel.fields.expirationDateOptional')}
            value={form.expirationDate}
            onChange={(e) => setForm((prev) => ({ ...prev, expirationDate: e.target.value }))}
            disabled={uploading}
          />
          <TextField
            fullWidth
            multiline
            minRows={2}
            label={t('projectDetails.otherDocuments.notes')}
            value={form.notes}
            onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
            disabled={uploading}
          />
          <Box>
            <Button variant="outlined" component="label" disabled={uploading}>
              {t('projectDetails.otherDocuments.selectFile')}
              <input
                hidden
                type="file"
                onChange={(e) => {
                  const selectedFile = e.target.files?.[0] || null;
                  setForm((prev) => ({ ...prev, file: selectedFile }));
                  e.target.value = '';
                }}
              />
            </Button>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {form.file
                ? `${t('projectDetails.otherDocuments.file')}: ${form.file.name}`
                : t('projectDetails.otherDocuments.selectFile')}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={uploading}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleUpload} disabled={!canSubmit}>
            {uploading ? t('projectDetails.otherDocuments.uploading') : t('common.upload')}
          </Button>
        </DialogActions>
      </Dialog>
      <UpdateFileDueDateDialog
        open={dueDateDialog.open}
        onClose={closeDueDateDialog}
        currentExpirationDate={dueDateDialog.document?.expirationDate}
        onSave={handleSaveDueDate}
        saving={savingDueDate}
      />
      <UpdateFileNotesDialog
        open={notesDialog.open}
        onClose={closeNotesDialog}
        currentNotes={notesDialog.document?.notes}
        onSave={handleSaveNotes}
        saving={savingNotes}
      />
    </Box>
  );
}

export default ProjectOtherDocumentsTab;
